import { call, put, SagaReturnType, takeEvery, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import adminModel, { Admin, CreateAdminDto, CreatePlugin } from '@/core/models/admin.model'
import { CreateEstablishmentDto, Establishment } from '@/core/models/establishment.model'
import { CreateStaffDto, Staff } from '@/core/models/staff.model'

import {
  createAdmin as createAdminAction,
  createAdminSuccess as createAdminSuccessAction,
  createAdminFailure as createAdminFailureAction,
  createEstablishment as createEstablishmentAction,
  createEstablishmentSuccess as createEstablishmentSuccessAction,
  createEstablishmentFailure as createEstablishmentFailureAction,
  deleteEstablishment as deleteEstablishmentAction,
  deleteEstablishmentSuccess as deleteEstablishmentSuccessAction,
  deleteEstablishmentFailure as deleteEstablishmentFailureAction,
  getEstablishments as getEstablishmentsAction,
  getEstablishmentsSuccess as getEstablishmentsSuccessAction,
  getEstablishmentsFailure as getEstablishmentsFailureAction,
  updateEstablishment as updateEstablishmentAction,
  updateEstablishmentSuccess as updateEstablishmentSuccessAction,
  updateEstablishmentFailure as updateEstablishmentFailureAction,
  getEstablishment as getEstablishmentAction,
  getEstablishmentSuccess as getEstablishmentSuccessAction,
  getEstablishmentFailure as getEstablishmentFailureAction,
  getStaffs as getStaffsAction,
  getStaffsSuccess as getStaffsSuccessAction,
  getStaffsFailure as getStaffsFailureAction,
  deleteStaff as deleteStaffAction,
  deleteStaffSuccess as deleteStaffSuccessAction,
  deleteStaffFailure as deleteStaffFailureAction,
  addStaff as addStaffAction,
  addStaffSuccess as addStaffSuccessAction,
  addStaffFailure as addStaffFailureAction,
  addPlugin as addPluginAction,
  addPluginSuccess as addPluginSuccessAction,
  addPluginFailure as addPluginFailureAction,
  testPlugin as testPluginAction,
  testPluginSuccess as testPluginSuccessAction,
  testPluginFailure as testPluginFailureAction,
  updateEstablishmentSubscription as updateEstablishmentSubscriptionAction,
  updateEstablishmentSubscriptionSuccess as updateEstablishmentSubscriptionSuccessAction,
  updateEstablishmentSubscriptionFailure as updateEstablishmentSubscriptionFailureAction,
  updateStaffSubscription as updateStaffSubscriptionAction,
  updateStaffSubscriptionSuccess as updateStaffSubscriptionSuccessAction,
  updateStaffSubscriptionFailure as updateStaffSubscriptionFailureAction,
  createOrdoclicAccount as createOrdoclicAccountAction,
  createOrdoclicAccountSuccess as createOrdoclicAccountSuccessAction,
  createOrdoclicAccountFailure as createOrdoclicAccountFailureAction,
  createOrdoclicEntity as createOrdoclicEntityAction,
  createOrdoclicEntitySuccess as createOrdoclicEntitySuccessAction,
  createOrdoclicEntityFailure as createOrdoclicEntityFailureAction,
  assignProfessionalsToOrdoclicEntity as assignProfessionalsToOrdoclicEntityAction,
  assignProfessionalsToOrdoclicEntitySuccess as assignProfessionalsToOrdoclicEntitySuccessAction,
  assignProfessionalsToOrdoclicEntityFailure as assignProfessionalsToOrdoclicEntityFailureAction,
} from '@/redux/reducers/admin.reducer'

import { handleError } from '@/utils/errors'
import { SearchRequestOptions } from '@/core/types/request'
import { SubscriptionType } from '@/utils/subscription/types'

function* createAdmin(action: PayloadAction<CreateAdminDto>) {
  try {
    const createAdminDto = action.payload
    const admin: Admin = yield call(adminModel.createAdmin, createAdminDto)
    yield put(createAdminSuccessAction(admin))
  } catch (exception) {
    yield put(createAdminFailureAction(handleError(exception)))
  }
}

function* createEstablishment(action: PayloadAction<CreateEstablishmentDto>) {
  try {
    const createEstablishmentDto = action.payload
    const establishment: Establishment = yield call(adminModel.createEstablishment, createEstablishmentDto)
    yield put(createEstablishmentSuccessAction(establishment))
  } catch (exception) {
    yield put(createEstablishmentFailureAction(handleError(exception)))
  }
}

function* updateEstablishment(action: PayloadAction<ReturnType<typeof updateEstablishmentAction>['payload']>) {
  try {
    const { updateEstablishmentDto, etablissementId } = action.payload
    const establishment: Establishment = yield call(adminModel.updateEstablishment, etablissementId, updateEstablishmentDto)
    yield put(updateEstablishmentSuccessAction(establishment))
  } catch (exception) {
    yield put(updateEstablishmentFailureAction(handleError(exception)))
  }
}

function* deleteEstablishment(action: PayloadAction<string>) {
  try {
    const establishmentId = action.payload
    yield call(adminModel.deleteEstablishment, establishmentId)
    yield put(deleteEstablishmentSuccessAction(establishmentId))
  } catch (exception) {
    yield put(deleteEstablishmentFailureAction(handleError(exception)))
  }
}

function* getEstablishments() {
  try {
    const establishments: Establishment[] = yield call(adminModel.getEstablishments)
    yield put(getEstablishmentsSuccessAction(establishments))
  } catch (exception) {
    yield put(getEstablishmentsFailureAction(handleError(exception)))
  }
}

function* getEstablishment() {
  try {
    const establishment: Establishment = yield call(adminModel.getEstablishment)
    yield put(getEstablishmentSuccessAction(establishment))
  } catch (exception) {
    yield put(getEstablishmentFailureAction(handleError(exception)))
  }
}

function* getStaffs(action: PayloadAction<{ filter: Record<string, any>; options?: SearchRequestOptions }>) {
  try {
    const { filter, options } = action.payload
    const [staffs, totalCount]: [Staff[], number] = yield call(adminModel.getStaffs, filter, options)
    yield put(
      getStaffsSuccessAction({
        staffs,
        totalCount,
      })
    )
  } catch (exception) {
    yield put(getStaffsFailureAction(handleError(exception)))
  }
}

function* deleteStaff(action: PayloadAction<{ staffId: string; associatedData?: string[] }>) {
  try {
    const { staffId, associatedData } = action.payload
    const staff: Staff = yield call(adminModel.deleteStaff, staffId, associatedData)
    yield put(deleteStaffSuccessAction(staff))
  } catch (exception) {
    yield put(deleteStaffFailureAction(handleError(exception)))
  }
}

function* addStaff(action: PayloadAction<CreateStaffDto>) {
  try {
    const createStaffDto = action.payload
    const staff: Staff = yield call(adminModel.addStaff, createStaffDto)
    yield put(addStaffSuccessAction(staff))
  } catch (exception) {
    yield put(addStaffFailureAction(handleError(exception)))
  }
}

function* addPlugin(action: PayloadAction<CreatePlugin>) {
  try {
    const createPluginDto = action.payload
    const plugin: Establishment = yield call(adminModel.addPlugin, createPluginDto)
    yield put(addPluginSuccessAction(plugin))
  } catch (exception) {
    yield put(addPluginFailureAction(handleError(exception)))
  }
}

function* testPlugin(action: PayloadAction<CreatePlugin>) {
  try {
    const plugin: boolean = yield call(adminModel.testPlugin, action.payload)
    yield put(testPluginSuccessAction(plugin))
  } catch (exception) {
    yield put(testPluginFailureAction(handleError(exception)))
  }
}

function* updateEstablishmentSubscription(action: PayloadAction<ReturnType<typeof updateEstablishmentSubscriptionAction>['payload']>) {
  try {
    const { subscription, etablissementId } = action.payload
    const establishment: Establishment = yield call(adminModel.updateEstablishmentSubscription, etablissementId, subscription)
    const [staffs, count]: SagaReturnType<typeof adminModel.getStaffs> = yield call(adminModel.getStaffs, { etablissementId })
    yield put(getStaffsSuccessAction({ staffs: staffs, totalCount: count }))
    yield put(updateEstablishmentSubscriptionSuccessAction(establishment))
  } catch (exception) {
    yield put(updateEstablishmentSubscriptionFailureAction(handleError(exception)))
  }
}

function* updateStaffSubscription(action: PayloadAction<{ staffId: string; subscription: SubscriptionType }>) {
  try {
    const { subscription, staffId } = action.payload
    const staff: Staff = yield call(adminModel.updateStaffSubscription, staffId, subscription)
    yield put(updateStaffSubscriptionSuccessAction(staff))
  } catch (exception) {
    yield put(updateStaffSubscriptionFailureAction(handleError(exception)))
  }
}

function* createOrdoclicAccount(action: PayloadAction<ReturnType<typeof createOrdoclicAccountAction>['payload']>) {
  try {
    const staffId = action.payload
    const staff: SagaReturnType<typeof adminModel.createOrdoclicAccount> = yield call(adminModel.createOrdoclicAccount, staffId)
    yield put(createOrdoclicAccountSuccessAction(staff))
  } catch (exception) {
    yield put(createOrdoclicAccountFailureAction(handleError(exception)))
  }
}

function* createOrdoclicEntity(action: PayloadAction<ReturnType<typeof createOrdoclicEntityAction>['payload']>) {
  try {
    const { establishmentId, name, finess } = action.payload;
    const establishment: Establishment = yield call(adminModel.createOrdoclicEntity, establishmentId, { name, finess });
    yield put(createOrdoclicEntitySuccessAction(establishment));
  } catch (exception) {
    yield put(createOrdoclicEntityFailureAction(handleError(exception)));
  }
}

function* assignProfessionalsToOrdoclicEntity(action: PayloadAction<string>) {
  try {
    const establishmentId = action.payload;
    const establishment: Establishment = yield call(adminModel.assignProfessionalsToOrdoclicEntity, establishmentId);
    yield put(assignProfessionalsToOrdoclicEntitySuccessAction(establishment));
  } catch (exception) {
    yield put(assignProfessionalsToOrdoclicEntityFailureAction(handleError(exception)));
  }
}

const sagas = [
  takeLatest(createAdminAction.type, createAdmin),
  takeLatest(createEstablishmentAction.type, createEstablishment),
  takeLatest(deleteEstablishmentAction.type, deleteEstablishment),
  takeLatest(getEstablishmentsAction.type, getEstablishments),
  takeLatest(getEstablishmentAction.type, getEstablishment),
  takeLatest(updateEstablishmentAction.type, updateEstablishment),
  takeLatest(getStaffsAction.type, getStaffs),
  takeEvery(deleteStaffAction.type, deleteStaff),
  takeLatest(addStaffAction.type, addStaff),
  takeLatest(testPluginAction.type, testPlugin),
  takeLatest(addPluginAction.type, addPlugin),
  takeLatest(updateEstablishmentSubscriptionAction.type, updateEstablishmentSubscription),
  takeLatest(updateStaffSubscriptionAction.type, updateStaffSubscription),
  takeLatest(createOrdoclicAccountAction.type, createOrdoclicAccount),
  takeLatest(createOrdoclicEntityAction.type, createOrdoclicEntity),
  takeLatest(assignProfessionalsToOrdoclicEntityAction.type, assignProfessionalsToOrdoclicEntity),
]

export default sagas
